import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Container, Typography, TextField, Grid, Button, Paper, IconButton, Stack, Divider, MenuItem, Select, InputLabel, FormControl, Alert, Chip, CircularProgress, Breadcrumbs, Link } from '@mui/material';
import { ArrowBack, Luggage } from '@mui/icons-material';
import api from '../config/api';
import { isFlightBookingEnabled } from '../config/features';
import StripeCheckout from '../components/StripeCheckout';
// FareClassSelector removed - fare selection now happens on dedicated page
import BaggageSelector from '../components/BaggageSelector';
import AuthModal from '../components/AuthModal';
import FlightSummaryCard from '../components/FlightSummaryCard';
import { openGoogleFlightsInNewTab } from '../utils/googleFlightsUrl';

function DuffelOfferInner() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const flightBookingEnabled = isFlightBookingEnabled();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offer, setOffer] = useState(null);
  const [stripeCheckoutOpen, setStripeCheckoutOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [customerPrice, setCustomerPrice] = useState(null);
  const MARKUP = 0.05; // 5% markup
  
  // For multi-leg round trips (Duffel-style)
  const outboundFlightId = searchParams.get('outboundFlightId');
  const inboundFlightId = searchParams.get('inboundFlightId');
  const [outboundOffer, setOutboundOffer] = useState(null);
  const [inboundOffer, setInboundOffer] = useState(null);
  
  // Fare class selection (separate for outbound and inbound)
  // availableFares and selectedFareId removed - fare selection now happens on dedicated page
  
  // Baggage selection
  const [baggageSelectorOpen, setBaggageSelectorOpen] = useState(false);
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedBaggage, setSelectedBaggage] = useState([]);
  
  // Auth modal
  const [authModalOpen, setAuthModalOpen] = useState(false);
  
  // Get route info from search params
  const origin = searchParams.get('origin') || '';
  const destination = searchParams.get('destination') || '';
  
  const initialCount = Math.max(parseInt(searchParams.get('passengers')) || 1, 1);
        const [passengers, setPassengers] = useState(
          Array.from({ length: initialCount }).map((_, idx) => ({
            id: `pas_${idx + 1}`,
            title: 'Mr',
            gender: 'unspecified',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            dateOfBirth: ''
          }))
        );
  
  // Validation state for each passenger
  const [validationErrors, setValidationErrors] = useState(
    Array.from({ length: initialCount }).map(() => ({}))
        );

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        setLoading(true);
        
        // Check if this is a multi-leg round trip (Duffel-style selection)
        if (outboundFlightId && inboundFlightId) {
          console.log('✈️ Fetching multi-leg round trip offers');
          console.log('  Outbound:', outboundFlightId);
          console.log('  Inbound:', inboundFlightId);
          
          // Fetch both offers in parallel
          const [outboundRes, inboundRes] = await Promise.all([
            api.get(`/duffel/offers/${outboundFlightId}`),
            api.get(`/duffel/offers/${inboundFlightId}`)
          ]);
          
          console.log('✈️ Outbound offer:', outboundRes.data.offer);
          console.log('✈️ Inbound offer:', inboundRes.data.offer);
          
          setOutboundOffer(outboundRes.data.offer);
          setInboundOffer(inboundRes.data.offer);
        } else {
          // Single offer (one-way or old bundled round-trip)
        const res = await api.get(`/duffel/offers/${offerId}`);
          console.log('✈️ Offer data:', res.data.offer);
          console.log('🧳 Baggage allowances:', res.data.offer?.baggage_allowances);
          console.log('📋 Conditions:', res.data.offer?.conditions);
        setOffer(res.data.offer);
          
          // Fetch fare alternatives (different cabin classes) for single offers only
          const origin = searchParams.get('origin');
          const destination = searchParams.get('destination');
          const departDate = searchParams.get('departDate');
          const returnDate = searchParams.get('returnDate');
          const passengersCount = searchParams.get('passengers') || '1';
          
          if (origin && destination && departDate) {
            try {
              const fareParams = new URLSearchParams({
                origin,
                destination,
                departure_date: departDate,
                passengers: passengersCount
              });
              
              // Only add return_date if it's after the departure_date
              if (returnDate) {
                const depDate = new Date(departDate);
                const retDate = new Date(returnDate);
                
                if (retDate > depDate) {
                  fareParams.append('return_date', returnDate);
                } else {
                  console.warn('⚠️ Return date is before departure date, skipping return_date for fare alternatives');
                }
              }
              
              const fareRes = await api.get(`/duffel/offers/${offerId}/fare-alternatives?${fareParams.toString()}`);
              console.log('🎫 Fare alternatives:', fareRes.data.alternatives);
              
              // Fare alternatives now handled on dedicated fare selection page
            } catch (fareError) {
              console.warn('⚠️ Could not fetch fare alternatives:', fareError);
            }
          }
        }
        
        // Fetch available services (baggage, seat selection, etc.)
        try {
          const servicesRes = await api.get(`/duffel/offers/${offerId}/available-services`);
          console.log('🎒 Available services:', servicesRes.data.services);
          setAvailableServices(servicesRes.data.services || []);
        } catch (serviceError) {
          console.warn('⚠️ Could not fetch available services:', serviceError);
          // Non-critical, continue without services
        }
      } catch (e) {
        console.error('❌ Error fetching offer:', e);
        setError(e.message || 'Failed to load offer');
      } finally {
        setLoading(false);
      }
    };
    fetchOffer();
  }, [offerId, searchParams, outboundFlightId, inboundFlightId]);

  // Calculate customer price with markup (including baggage)
  useEffect(() => {
    // For multi-leg round trips, calculate combined total
    if (outboundOffer && inboundOffer) {
      const outboundCost = parseFloat(outboundOffer.total_amount);
      const inboundCost = parseFloat(inboundOffer.total_amount);
      const baggageCost = selectedBaggage.reduce((sum, bag) => 
        sum + (parseFloat(bag.total_amount) * bag.quantity), 0
      );
      const totalCost = outboundCost + inboundCost + baggageCost;
      const markup = totalCost * MARKUP;
      setCustomerPrice((totalCost + markup).toFixed(2));
    } else if (offer) {
      // Single offer
      const duffelCost = parseFloat(offer.total_amount);
      const baggageCost = selectedBaggage.reduce((sum, bag) => 
        sum + (parseFloat(bag.total_amount) * bag.quantity), 0
      );
      const totalCost = duffelCost + baggageCost;
      const markup = totalCost * MARKUP;
      const total = totalCost + markup;
      setCustomerPrice(total.toFixed(2));
      console.log('💰 Pricing:', {
        duffelCost,
        baggageCost: baggageCost.toFixed(2),
        totalBeforeMarkup: totalCost.toFixed(2),
        markup: `${MARKUP * 100}%`,
        markupAmount: markup.toFixed(2),
        customerPays: total.toFixed(2)
      });
    }
  }, [offer, outboundOffer, inboundOffer, selectedBaggage, MARKUP]);

  const handleBack = () => {
    // Build return URL with all search parameters to preserve search state
    const origin = searchParams.get('origin');
    const destination = searchParams.get('destination');
    const departDate = searchParams.get('departDate');
    const returnDate = searchParams.get('returnDate');
    const passengersCount = searchParams.get('passengers');
    const tripType = searchParams.get('tripType');
    
    // If we have search params, go back to the flight search results page
    if (origin && destination && departDate) {
      const params = new URLSearchParams();
      params.set('origin', origin);
      params.set('destination', destination);
      params.set('departDate', departDate);
      if (returnDate) params.set('returnDate', returnDate);
      if (passengersCount) params.set('passengers', passengersCount);
      if (tripType) params.set('tripType', tripType);
      
      navigate(`/flights/search?${params.toString()}`);
    } else {
      // Otherwise, go back to deals page
      navigate('/deals?tab=flights');
    }
  };

  // Validation functions
  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
      case 'lastName':
        if (!value || value.trim().length === 0) {
          return 'This field is required';
        }
        if (!/^[a-zA-Z\s'-]+$/.test(value)) {
          return 'Only letters, spaces, hyphens, and apostrophes allowed';
        }
        if (value.trim().length < 2) {
          return 'Must be at least 2 characters';
        }
        return null;
      
      case 'email':
        if (!value || value.trim().length === 0) {
          return 'Email is required';
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Invalid email format';
        }
        return null;
      
      case 'phone':
        if (!value || value.trim().length === 0) {
          return 'Phone number is required';
        }
        // Remove all non-digit characters except +
        const cleanPhone = value.replace(/[^\d+]/g, '');
        if (!cleanPhone.startsWith('+')) {
          return 'Phone must start with + (e.g., +1 for US)';
        }
        if (cleanPhone.length < 10) {
          return 'Phone number too short';
        }
        if (cleanPhone.length > 16) {
          return 'Phone number too long';
        }
        return null;
      
      case 'dateOfBirth':
        if (!value) {
          return 'Date of birth is required';
        }
        const dob = new Date(value);
        const today = new Date();
        const age = Math.floor((today - dob) / (365.25 * 24 * 60 * 60 * 1000));
        
        if (isNaN(dob.getTime())) {
          return 'Invalid date';
        }
        if (dob > today) {
          return 'Date cannot be in the future';
        }
        if (age < 18) {
          return 'Passenger must be at least 18 years old';
        }
        if (age > 120) {
          return 'Please enter a valid date';
        }
        return null;
      
      default:
        return null;
    }
  };

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    const next = [...passengers];
    next[index] = { ...next[index], [name]: value };
    setPassengers(next);
    
    // Clear validation error when user types
    const nextErrors = [...validationErrors];
    if (nextErrors[index][name]) {
      nextErrors[index] = { ...nextErrors[index], [name]: null };
      setValidationErrors(nextErrors);
    }
  };

  const handleBlur = (index, e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    
    if (error) {
      const nextErrors = [...validationErrors];
      nextErrors[index] = { ...nextErrors[index], [name]: error };
      setValidationErrors(nextErrors);
    }
  };

  const validateAllPassengers = () => {
    const fields = ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth'];
    let isValid = true;
    const newErrors = passengers.map((passenger) => {
      const errors = {};
      fields.forEach((field) => {
        const error = validateField(field, passenger[field]);
        if (error) {
          errors[field] = error;
          isValid = false;
        }
      });
      return errors;
    });
    
    setValidationErrors(newErrors);
    return isValid;
  };

  const getGoogleFlightsParams = () => {
    const departDate = searchParams.get('departDate')
      || offer?.slices?.[0]?.segments?.[0]?.departing_at?.split('T')[0]
      || outboundOffer?.slices?.[0]?.segments?.[0]?.departing_at?.split('T')[0]
      || '';
    const returnDate = searchParams.get('returnDate')
      || offer?.slices?.[1]?.segments?.[0]?.departing_at?.split('T')[0]
      || inboundOffer?.slices?.[0]?.segments?.[0]?.departing_at?.split('T')[0]
      || '';
    const originCode = searchParams.get('origin')
      || offer?.slices?.[0]?.segments?.[0]?.origin?.iata_code
      || outboundOffer?.slices?.[0]?.segments?.[0]?.origin?.iata_code
      || origin;
    const destinationCode = searchParams.get('destination')
      || offer?.slices?.[0]?.segments?.[offer?.slices?.[0]?.segments?.length - 1]?.destination?.iata_code
      || outboundOffer?.slices?.[0]?.segments?.[outboundOffer?.slices?.[0]?.segments?.length - 1]?.destination?.iata_code
      || destination;
    const passengersCount = searchParams.get('passengers') || String(passengers.length);

    return {
      origin: originCode,
      destination: destinationCode,
      departDate,
      returnDate,
      passengers: passengersCount,
    };
  };

  const handleExternalFlightBooking = () => {
    const opened = openGoogleFlightsInNewTab(getGoogleFlightsParams());
    if (!opened) {
      setError(t('flightBooking.missingSearchParams'));
    }
  };

  const handleBook = async () => {
    try {
      setError(null);

      if (!flightBookingEnabled) {
        handleExternalFlightBooking();
        return;
      }
      
      // Check if user is logged in before booking
      const token = localStorage.getItem('token');
      if (!token) {
        // Show auth modal instead of redirecting
        setAuthModalOpen(true);
        return;
      }
      
      // Validate all passengers before proceeding
      if (!validateAllPassengers()) {
        setError('Please fix all validation errors before booking');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      
      setLoading(true);
      
      // Calculate total with extra baggage
      const baggageCost = selectedBaggage.reduce((sum, bag) => 
        sum + (parseFloat(bag.total_amount) * bag.quantity), 0
      );
      
      // For multi-leg trips, add both outbound and inbound costs
      let baseFareCost;
      let offerCurrency;
      let activeOfferId;
      
      if (outboundOffer && inboundOffer) {
        // Validate that both offers have total_amount and IDs
        if (!outboundOffer.total_amount || !inboundOffer.total_amount) {
          throw new Error('Flight offer data is incomplete. Please go back and select flights again.');
        }
        if (!outboundOffer.id || !inboundOffer.id) {
          throw new Error('Flight offer IDs are missing. Please go back and select flights again.');
        }
        
        baseFareCost = parseFloat(outboundOffer.total_amount) + parseFloat(inboundOffer.total_amount);
        offerCurrency = outboundOffer.total_currency;
        // For multi-leg, use the outbound offer ID as primary (backend needs to be updated to handle both)
        activeOfferId = outboundOffer.id;
        console.log('💰 Multi-leg booking:', {
          outboundId: outboundOffer.id,
          inboundId: inboundOffer.id,
          outbound: outboundOffer.total_amount,
          inbound: inboundOffer.total_amount,
          combined: baseFareCost,
          baggage: baggageCost
        });
      } else if (offer) {
        if (!offer.total_amount) {
          throw new Error('Flight offer data is incomplete. Please go back and select a flight again.');
        }
        baseFareCost = parseFloat(offer.total_amount);
        offerCurrency = offer.total_currency;
        activeOfferId = offerId;
      } else {
        throw new Error('No flight offer data available. Please go back and select flights.');
      }
      
      const totalDuffelCost = baseFareCost + baggageCost;
      
      // LOCAL DEV: Skip Stripe, directly create booking (test mode always succeeds)
      const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isLocalDev) {
        console.log('🧪 LOCAL DEV: Bypassing Stripe, creating booking directly...');
        
        // Map passengers to backend format
        const mappedPassengers = passengers.map(p => ({
          type: 'adult',
          title: p.title || 'mr',
          gender: p.gender || 'm',
          firstName: p.firstName,
          lastName: p.lastName,
          dateOfBirth: p.dateOfBirth,
          email: p.email,
          phone: p.phone && p.phone.startsWith('+') ? p.phone : `+${p.phone || '1234567890'}`
        }));
        
        // Directly create booking (bypassing Stripe)
        console.log('📤 Sending booking request...');
        console.log('   Total Duffel Cost:', totalDuffelCost);
        console.log('   With 5% markup:', (totalDuffelCost * (1 + MARKUP)).toFixed(2));
        
        const bookingPayload = {
          offerId: activeOfferId, // Single offer ID (may contain 2 slices for round trips)
          passengers: mappedPassengers,
          duffelCost: totalDuffelCost.toString(),
          currency: offerCurrency,
          markup: MARKUP,
          services: selectedBaggage.map(bag => ({
            id: bag.id,
            quantity: bag.quantity,
            type: bag.type
          }))
        };
        
        const bookingResponse = await api.post('/payments/test-flight-booking', bookingPayload);
        
        console.log('✅ Booking response received:', bookingResponse.data);
        setLoading(false);
        
        if (bookingResponse.data.success && bookingResponse.data.sessionId) {
          console.log('✅ Navigating to confirmation...');
          navigate(`/booking/confirmation?session_id=${bookingResponse.data.sessionId}`);
        } else {
          throw new Error(bookingResponse.data.error || 'Booking failed');
        }
        
        return;
      }
      
      // PRODUCTION: Use Stripe Payment Intent flow
      console.log('💳 Creating Stripe payment authorization...');
      
      const checkoutPayload = {
        offerId: activeOfferId, // Single offer ID (may contain 2 slices for round trips)
        passengers,
        duffelCost: totalDuffelCost.toString(),
        currency: offerCurrency,
        markup: MARKUP,
        services: selectedBaggage.map(bag => ({
          id: bag.id,
          quantity: bag.quantity,
          type: bag.type
        }))
      };
      
      const response = await api.post('/payments/create-flight-checkout', checkoutPayload);
      
      if (!response.data.clientSecret) {
        throw new Error('No client secret returned from server');
      }
      
      setClientSecret(response.data.clientSecret);
      setStripeCheckoutOpen(true)
    } catch (e) {
      console.error('❌ Error creating payment authorization:', e);
      console.error('❌ Error response:', e.response);
      console.error('❌ Error data:', e.response?.data);
      setError(e.response?.data?.error || e.message || 'Failed to create payment authorization');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    try {
      if (!flightBookingEnabled) {
        return;
      }

      console.log('✅ Step 2: Payment authorized! Creating Duffel booking...');
      setStripeCheckoutOpen(false);
      setLoading(true);
      
      // Map passengers to backend format
      const mappedPassengers = passengers.map(p => ({
        type: 'adult',
        title: p.title || 'mr',
        gender: p.gender || 'm',
        firstName: p.firstName,
        lastName: p.lastName,
        dateOfBirth: p.dateOfBirth,
        email: p.email,
        phone: p.phone && p.phone.startsWith('+') ? p.phone : `+${p.phone || '1234567890'}`
      }));
      
      // Calculate total with extra baggage
      const baggageCost = selectedBaggage.reduce((sum, bag) => 
        sum + (parseFloat(bag.total_amount) * bag.quantity), 0
      );
      const totalDuffelCost = parseFloat(offer.total_amount) + baggageCost;
      
      // Create Duffel booking (payment already authorized)
      const bookingResponse = await api.post('/payments/complete-flight-booking', {
        offerId: offerId,
        passengers: mappedPassengers,
        duffelCost: totalDuffelCost.toString(),
        currency: offer.total_currency,
        markup: MARKUP,
        services: selectedBaggage.map(bag => ({
          id: bag.id,
          quantity: bag.quantity,
          type: bag.type
        }))
      });
      
      console.log('✅ Step 3: Booking confirmed!', bookingResponse.data);
      setLoading(false);
      
      if (bookingResponse.data.success && bookingResponse.data.sessionId) {
        console.log('🔄 Step 4: Navigating to confirmation page...');
        navigate(`/booking/confirmation?session_id=${bookingResponse.data.sessionId}`);
      } else {
        throw new Error(bookingResponse.data.error || 'Booking failed');
      }
    } catch (e) {
      console.error('❌ Error completing booking:', e);
      const errorMessage = e.response?.data?.error || e.message || 'Failed to complete booking';
      
      // Check if offer expired
      if (errorMessage.includes('no longer available') || errorMessage.includes('expired') || errorMessage.includes('Please select another offer')) {
        setError('⏰ This flight offer has expired. Flight prices and availability change frequently. Please search again to see current options.');
      } else {
        setError(errorMessage);
      }
      setLoading(false);
    }
  };

        const parseAmount = (val) => {
          if (!val) return 0;
          if (typeof val === 'number') return val;
          if (typeof val === 'string') {
            const num = parseFloat(val.replace(/[^0-9.]/g, ''));
            return isNaN(num) ? 0 : num;
          }
          return 0;
        };

        const currency = offer?.total_currency || (offer?.price ? (offer.price.split(' ')[0] || '') : '');
        const unitAmount = parseAmount(offer?.total_amount || offer?.price);
        const totalAmount = unitAmount * passengers.length;

        return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Paper sx={{ p: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 3 }}>Loading flight details...</Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: error.includes('expired') ? 2 : 0 }}>
              {error}
            </Typography>
            {error.includes('expired') && (
              <Button 
                variant="contained" 
                onClick={() => navigate(-1)}
                sx={{ mt: 1 }}
              >
                ← Search Again
              </Button>
            )}
          </Alert>
        )}
        
        {/* Multi-leg round trip checkout (reached from fare selector) */}
        {!loading && outboundOffer && inboundOffer && outboundOffer.total_amount && inboundOffer.total_amount && window.location.pathname === '/flights/checkout' && (
          <>
            {/* Header with Back Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton 
                onClick={handleBack}
                sx={{ mr: 2 }}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Review Flight & Select Fare
              </Typography>
            </Box>
            
            {/* Breadcrumb */}
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs separator="›" aria-label="breadcrumb">
                <Typography variant="body2" color="text.secondary">
                  Orders
                </Typography>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/deals?tab=flights')}
                  sx={{ 
                    textDecoration: 'none',
                    color: 'primary.main',
                    '&:hover': { textDecoration: 'underline' },
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit'
                  }}
                >
                  New order
                </Link>
                <Typography variant="body2" color="text.secondary">
                  {origin && destination ? `${origin} to ${destination}` : 'Select flights'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Checkout
                </Typography>
              </Breadcrumbs>
            </Box>

            {/* Flight Summary Card */}
            <FlightSummaryCard
              outboundFlight={outboundOffer.slices && outboundOffer.slices[0] ? {
                ...outboundOffer.slices[0],
                passengers: outboundOffer.passengers,
                baggage_allowances: outboundOffer.passengers?.[0]?.baggage_allowances || []
              } : null}
              inboundFlight={inboundOffer.slices && inboundOffer.slices[0] ? {
                ...inboundOffer.slices[0],
                passengers: inboundOffer.passengers,
                baggage_allowances: inboundOffer.passengers?.[0]?.baggage_allowances || []
              } : null}
              totalPrice={`${outboundOffer.total_currency} ${((parseFloat(outboundOffer.total_amount) + parseFloat(inboundOffer.total_amount)) * 1.05).toFixed(2)}`}
              showPrice={true}
            />

            {/* Main Layout: Left column (flight details) + Right column (scrollable form) */}
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
              
              {/* LEFT: Flight Details (sticky) */}
              <Box sx={{ flex: '0 0 400px', position: 'sticky', top: 20 }}>
                {/* Trip Header */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {outboundOffer.origin} ⇄ {outboundOffer.destination}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(outboundOffer.departure.time).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  </Typography>
                </Box>

                {/* Selected Flights Section */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Selected flights
                </Typography>

                {/* Outbound Flight Card */}
                <Paper sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    {outboundOffer.airline_logo ? (
                      <Box sx={{ width: 48, height: 48, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.5 }}>
                        <img 
                          src={outboundOffer.airline_logo} 
                          alt={outboundOffer.airline}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ width: 48, height: 48, bgcolor: '#5B21B6', borderRadius: 1, mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem' }}>
                          {outboundOffer.airline?.substring(0, 2) || 'FL'}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {new Date(outboundOffer.departure.time).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                        {' '}
                        {new Date(outboundOffer.departure.time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                        {' – '}
                        {new Date(outboundOffer.arrival.time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                        {outboundOffer.arrival.time.split('T')[0] !== outboundOffer.departure.time.split('T')[0] && <sup>+1</sup>}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {outboundOffer.cabin_class || 'Basic'} · {outboundOffer.airline}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {outboundOffer.duration}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {outboundOffer.stops === 0 ? 'Non-stop' : `${outboundOffer.stops} stop${outboundOffer.stops > 1 ? 's' : ''}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {outboundOffer.origin} – {outboundOffer.destination}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Flight Timeline */}
                  <Box sx={{ pl: 0, mt: 3 }}>
                    {/* Departure */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '3px solid #666', mr: 2.5, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                          {new Date(outboundOffer.departure.time).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}, {new Date(outboundOffer.departure.time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Depart from {outboundOffer.departure.city || outboundOffer.departure.airport} ({outboundOffer.departure.airport})
                          {outboundOffer.departure.terminal && `, Terminal ${outboundOffer.departure.terminal}`}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Flight Info */}
                    <Box sx={{ borderLeft: '3px solid #e0e0e0', ml: '4px', pl: 3.5, py: 1.5, mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Flight duration: {outboundOffer.duration}
                      </Typography>
                      {outboundOffer.segments && outboundOffer.segments[0] && (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {outboundOffer.segments[0].marketing_carrier || outboundOffer.airline}
                            {' '}
                            {outboundOffer.segments[0].marketing_carrier_code || ''}{outboundOffer.segments[0].marketing_flight_number || ''}
                          </Typography>
                          {outboundOffer.segments[0].aircraft && (
                            <Typography variant="caption" color="text.secondary">
                              {outboundOffer.segments[0].aircraft}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                    
                    {/* Arrival */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '3px solid #666', mr: 2.5, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                          {new Date(outboundOffer.arrival.time).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}, {new Date(outboundOffer.arrival.time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Arrive at {outboundOffer.arrival.city || outboundOffer.arrival.airport} ({outboundOffer.arrival.airport})
                          {outboundOffer.arrival.terminal && `, Terminal ${outboundOffer.arrival.terminal}`}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Tags */}
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ px: 1.5, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, fontWeight: 500 }}>
                        {outboundOffer.cabin_class || 'Economy'}
                      </Typography>
                      <Typography variant="caption" sx={{ px: 1.5, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, fontWeight: 500 }}>
                        {outboundOffer.airline}
                      </Typography>
                      {outboundOffer.segments && outboundOffer.segments[0]?.marketing_flight_number && (
                        <Typography variant="caption" sx={{ px: 1.5, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, fontWeight: 500 }}>
                          {outboundOffer.segments[0].marketing_carrier_code || ''}{outboundOffer.segments[0].marketing_flight_number}
                        </Typography>
                      )}
                      {outboundOffer.baggage_allowances && outboundOffer.baggage_allowances.length > 0 && (
                        <>
                          <Typography variant="caption" sx={{ px: 1.5, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, fontWeight: 500 }}>
                            🧳 {outboundOffer.baggage_allowances[0]?.carry_on_quantity || 1} carry-on bag
                          </Typography>
                          {outboundOffer.baggage_allowances[0]?.checked_bag_quantity > 0 && (
                            <Typography variant="caption" sx={{ px: 1.5, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, fontWeight: 500 }}>
                              💼 {outboundOffer.baggage_allowances[0].checked_bag_quantity} checked bag
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                </Paper>

                {/* Inbound Flight Card */}
                <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    {inboundOffer.airline_logo ? (
                      <Box sx={{ width: 48, height: 48, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.5 }}>
                        <img 
                          src={inboundOffer.airline_logo} 
                          alt={inboundOffer.airline}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ width: 48, height: 48, bgcolor: '#5B21B6', borderRadius: 1, mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem' }}>
                          {inboundOffer.airline?.substring(0, 2) || 'FL'}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {new Date(inboundOffer.departure.time).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                        {' '}
                        {new Date(inboundOffer.departure.time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                        {' – '}
                        {new Date(inboundOffer.arrival.time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                        {inboundOffer.arrival.time.split('T')[0] !== inboundOffer.departure.time.split('T')[0] && <sup>+1</sup>}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {inboundOffer.cabin_class || 'Basic'} · {inboundOffer.airline}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {inboundOffer.duration}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {inboundOffer.stops === 0 ? 'Non-stop' : `${inboundOffer.stops} stop${inboundOffer.stops > 1 ? 's' : ''}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {inboundOffer.origin} – {inboundOffer.destination}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Flight Timeline */}
                  <Box sx={{ pl: 0, mt: 3 }}>
                    {/* Departure */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '3px solid #666', mr: 2.5, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                          {new Date(inboundOffer.departure.time).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}, {new Date(inboundOffer.departure.time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Depart from {inboundOffer.departure.city || inboundOffer.departure.airport} ({inboundOffer.departure.airport})
                          {inboundOffer.departure.terminal && `, Terminal ${inboundOffer.departure.terminal}`}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Flight Info */}
                    <Box sx={{ borderLeft: '3px solid #e0e0e0', ml: '4px', pl: 3.5, py: 1.5, mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Flight duration: {inboundOffer.duration}
                      </Typography>
                      {inboundOffer.segments && inboundOffer.segments[0] && (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {inboundOffer.segments[0].marketing_carrier || inboundOffer.airline}
                            {' '}
                            {inboundOffer.segments[0].marketing_carrier_code || ''}{inboundOffer.segments[0].marketing_flight_number || ''}
                          </Typography>
                          {inboundOffer.segments[0].aircraft && (
                            <Typography variant="caption" color="text.secondary">
                              {inboundOffer.segments[0].aircraft}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                    
                    {/* Arrival */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '3px solid #666', mr: 2.5, mt: 0.5, flexShrink: 0 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                          {new Date(inboundOffer.arrival.time).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}, {new Date(inboundOffer.arrival.time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Arrive at {inboundOffer.arrival.city || inboundOffer.arrival.airport} ({inboundOffer.arrival.airport})
                          {inboundOffer.arrival.terminal && `, Terminal ${inboundOffer.arrival.terminal}`}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Tags */}
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ px: 1.5, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, fontWeight: 500 }}>
                        {inboundOffer.cabin_class || 'Economy'}
                      </Typography>
                      <Typography variant="caption" sx={{ px: 1.5, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, fontWeight: 500 }}>
                        {inboundOffer.airline}
                      </Typography>
                      {inboundOffer.segments && inboundOffer.segments[0]?.marketing_flight_number && (
                        <Typography variant="caption" sx={{ px: 1.5, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, fontWeight: 500 }}>
                          {inboundOffer.segments[0].marketing_carrier_code || ''}{inboundOffer.segments[0].marketing_flight_number}
                        </Typography>
                      )}
                      {inboundOffer.baggage_allowances && inboundOffer.baggage_allowances.length > 0 && (
                        <>
                          <Typography variant="caption" sx={{ px: 1.5, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, fontWeight: 500 }}>
                            🧳 {inboundOffer.baggage_allowances[0]?.carry_on_quantity || 1} carry-on bag
                          </Typography>
                          {inboundOffer.baggage_allowances[0]?.checked_bag_quantity > 0 && (
                            <Typography variant="caption" sx={{ px: 1.5, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1, fontWeight: 500 }}>
                              💼 {inboundOffer.baggage_allowances[0].checked_bag_quantity} checked bag
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                </Paper>

                {/* Pricing Summary */}
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Base fare</Typography>
                    <Typography variant="body2">
                      {(parseFloat(outboundOffer.total_amount) + parseFloat(inboundOffer.total_amount)).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Taxes & fees</Typography>
                    <Typography variant="body2">Included in fare</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Service charge (5%)</Typography>
                    <Typography variant="body2">
                      {((parseFloat(outboundOffer.total_amount) + parseFloat(inboundOffer.total_amount)) * MARKUP).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Total ({outboundOffer.total_currency})</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {((parseFloat(outboundOffer.total_amount) + parseFloat(inboundOffer.total_amount)) * (1 + MARKUP)).toFixed(2)}
                    </Typography>
                  </Box>
                </Paper>
              </Box>

              {/* RIGHT: Scrollable Checkout Form */}
              {flightBookingEnabled ? (
              <Box sx={{ flex: 1 }}>
                
                {/* Paying now or later */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Paying now, or later?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Decide whether you want to pay for your trip now in its entirety, or whether you'd like to put a hold on the order, and pay at a later date. Be aware that you cannot currently select seats or baggage when holding an order.
                  </Typography>
                  {/* Placeholder for payment option selector */}
                </Box>

                {/* Contact Details Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Contact details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Email *"
                        type="email"
                        name="email"
                        value={passengers[0]?.email || ''}
                        onChange={(e) => handleChange(0, e)}
                        onBlur={(e) => handleBlur(0, e)}
                        error={!!validationErrors[0]?.email}
                        helperText={validationErrors[0]?.email}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Phone number *"
                        type="tel"
                        name="phone"
                        value={passengers[0]?.phone || ''}
                        onChange={(e) => handleChange(0, e)}
                        onBlur={(e) => handleBlur(0, e)}
                        error={!!validationErrors[0]?.phone}
                        helperText={validationErrors[0]?.phone}
                        placeholder="+1 617 756 2626"
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Passengers Section */}
                {passengers.map((passenger, idx) => (
                  <Box key={passenger.id} sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Passengers
                    </Typography>
                    <Chip label={`Adult ${idx + 1}`} sx={{ mb: 2 }} />
                    
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Personal details
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={2}>
                        <TextField
                          select
                          fullWidth
                          label="Title *"
                          name="title"
                          value={passenger.title}
                          onChange={(e) => handleChange(idx, e)}
                        >
                          <MenuItem value="Mr">Mr.</MenuItem>
                          <MenuItem value="Mrs">Mrs.</MenuItem>
                          <MenuItem value="Ms">Ms.</MenuItem>
                          <MenuItem value="Dr">Dr.</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          fullWidth
                          label="Given name *"
                          name="firstName"
                          value={passenger.firstName}
                          onChange={(e) => handleChange(idx, e)}
                          onBlur={(e) => handleBlur(idx, e)}
                          error={!!validationErrors[idx]?.firstName}
                          helperText={validationErrors[idx]?.firstName}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          fullWidth
                          label="Family name *"
                          name="lastName"
                          value={passenger.lastName}
                          onChange={(e) => handleChange(idx, e)}
                          onBlur={(e) => handleBlur(idx, e)}
                          error={!!validationErrors[idx]?.lastName}
                          helperText={validationErrors[idx]?.lastName}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Date of birth *"
                          type="date"
                          name="dateOfBirth"
                          value={passenger.dateOfBirth}
                          onChange={(e) => handleChange(idx, e)}
                          onBlur={(e) => handleBlur(idx, e)}
                          error={!!validationErrors[idx]?.dateOfBirth}
                          helperText={validationErrors[idx]?.dateOfBirth}
                          InputLabelProps={{ shrink: true }}
                          placeholder="DD / MM / YYYY"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          select
                          fullWidth
                          label="Gender *"
                          name="gender"
                          value={passenger.gender}
                          onChange={(e) => handleChange(idx, e)}
                        >
                          <MenuItem value="m">Male</MenuItem>
                          <MenuItem value="f">Female</MenuItem>
                          <MenuItem value="unspecified">Prefer not to say</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>

                    {/* Passport details placeholder */}
                    <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
                      Passport details
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Passport details are optional for domestic flights
                    </Alert>
                  </Box>
                ))}

                {/* Add Extras */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Add extras
                  </Typography>
                  
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>Extra baggage</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add any extra baggage you need for your trip
                      </Typography>
                    </Box>
                    <Chip label="Not available" />
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>Seat selection</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Specify where on the plane you'd like to sit
                      </Typography>
                    </Box>
                    <Chip label="Not available" />
                  </Paper>
                </Box>

                {/* Payment Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Payment
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Description</Typography>
                    <Typography variant="body2">Price (USD)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Base fare (incl. taxes)</Typography>
                    <Typography variant="body2">
                      {(parseFloat(outboundOffer.total_amount) + parseFloat(inboundOffer.total_amount)).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Service charge (5%)</Typography>
                    <Typography variant="body2">
                      {((parseFloat(outboundOffer.total_amount) + parseFloat(inboundOffer.total_amount)) * MARKUP).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Total (USD)</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {((parseFloat(outboundOffer.total_amount) + parseFloat(inboundOffer.total_amount)) * (1 + MARKUP)).toFixed(2)}
                    </Typography>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleBook}
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      bgcolor: 'black',
                      color: 'white',
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': { bgcolor: '#333' }
                    }}
                  >
                    {loading ? t('flightBooking.processing') : t('flightBooking.purchaseTickets')}
                  </Button>
                </Box>

              </Box>
              ) : (
                <Box sx={{ flex: 1 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('flightBooking.externalBookingNotice')}
                  </Alert>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('flightBooking.externalBookingHelp')}
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleBook}
                    sx={{
                      py: 1.5,
                      bgcolor: 'black',
                      color: 'white',
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': { bgcolor: '#333' }
                    }}
                  >
                    {t('flightBooking.bookOnGoogleFlights')}
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
        
        {/* Single offer display (old flow) */}
        {offer && !outboundOffer && !inboundOffer && (
          <>
            {/* Header with Back Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton 
                onClick={handleBack} 
                sx={{ mr: 2, color: 'primary.main' }}
                aria-label="Go back"
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Review Flight</Typography>
            </Box>
            
            {/* Breadcrumb */}
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs separator="›" aria-label="breadcrumb">
                <Typography variant="body2" color="text.secondary">
                  Orders
                </Typography>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/deals?tab=flights')}
                  sx={{ 
                    textDecoration: 'none',
                    color: 'primary.main',
                    '&:hover': { textDecoration: 'underline' },
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit'
                  }}
                >
                  New order
                </Link>
                <Typography variant="body2" color="text.secondary">
                  {origin && destination ? `${origin} to ${destination}` : 'Select flights'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Checkout
                </Typography>
              </Breadcrumbs>
            </Box>

            {/* Flight Summary Card */}
            <FlightSummaryCard
              outboundFlight={offer.slices && offer.slices[0] ? {
                ...offer.slices[0],
                passengers: offer.passengers,
                baggage_allowances: offer.passengers?.[0]?.baggage_allowances || []
              } : null}
              inboundFlight={offer.slices && offer.slices[1] ? {
                ...offer.slices[1],
                passengers: offer.passengers,
                baggage_allowances: offer.passengers?.[0]?.baggage_allowances || []
              } : null}
              totalPrice={offer.total_currency && offer.total_amount ? `${offer.total_currency} ${(parseFloat(offer.total_amount) * 1.05).toFixed(2)}` : null}
              showPrice={true}
            />

            {!flightBookingEnabled ? (
              <Box sx={{ mt: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('flightBooking.externalBookingNotice')}
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('flightBooking.externalBookingHelp')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBook}
                  fullWidth
                  size="large"
                  sx={{
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                    }
                  }}
                >
                  {t('flightBooking.bookOnGoogleFlights')}
                </Button>
              </Box>
            ) : (
              <>
            {false && <Paper sx={{ p: 3, mb: 3, backgroundColor: 'grey.50' }}>
              <Stack spacing={2}>
                {/* Main Flight Info with Airline Logo */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {/* Airline Logo */}
                    {offer.airline_logo && (
                      <Box sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'white', borderRadius: 1, p: 0.5 }}>
                        <img 
                          src={offer.airline_logo} 
                          alt={offer.airline}
                          style={{ maxWidth: '100%', maxHeight: '100%' }}
                        />
                      </Box>
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {offer.airline} — {offer.origin} → {offer.destination}
                        {offer.return_flight && ` → ${offer.origin}`}
                  </Typography>
                        <Stack direction="row" spacing={2} alignItems="baseline">
                          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {offer.price}
                          </Typography>
                          {passengers.length > 1 && (
                            <Typography variant="body1" sx={{ color: '#059669', fontWeight: 700 }}>
                              Total ({passengers.length}): {currency} {totalAmount.toFixed(2)}
                            </Typography>
                          )}
                        </Stack>
                    </Box>
                  </Box>
                </Box>

                {/* Outbound Flight Details */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                    Outbound: {offer.origin} → {offer.destination}
                  </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Departure
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {new Date(offer.departure_time).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(offer.departure_time).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      })}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Arrival
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {new Date(offer.arrival_time).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(offer.arrival_time).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      })}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {offer.duration}
                    </Typography>
                    {offer.flight_number && (
                      <Typography variant="caption" color="text.secondary">
                        Flight {offer.flight_number}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Stops
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {offer.stops} {offer.stops === 1 ? 'stop' : 'stops'}
                    </Typography>
                  </Grid>
                </Grid>

                  {/* Outbound Stops Details */}
                      {(offer.segments && Array.isArray(offer.segments) && offer.segments.length > 1) && (
                        <Box sx={{ mt: 2 }}>
                          <Divider sx={{ mb: 1 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Connection details
                          </Typography>
                          {offer.segments.map((seg, idx) => {
                            const depTime = seg?.origin?.time || seg?.departure_time || seg?.departure || '';
                            const arrTime = seg?.destination?.time || seg?.arrival_time || seg?.arrival || '';
                            const depAirport = seg?.origin?.airport || seg?.origin || '';
                            const arrAirport = seg?.destination?.airport || seg?.destination || '';
                            return (
                              <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {depAirport} → {arrAirport}
                                </Typography>
                                <Typography variant="body2">
                                  {depTime ? new Date(depTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : '--:--'}
                                  {' '}–{' '}
                                  {arrTime ? new Date(arrTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : '--:--'}
                                </Typography>
                              </Box>
                            );
                          })}
                    </Box>
                  )}
                </Box>

                {/* Return Flight Details */}
                {offer.return_flight && (
                  <Box sx={{ mt: 3, pt: 3, borderTop: '2px solid #e0e0e0' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                      Return: {offer.return_flight.departure.airport} → {offer.return_flight.arrival.airport}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          Departure
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Date(offer.return_flight.departure.time).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(offer.return_flight.departure.time).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          })}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          Arrival
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Date(offer.return_flight.arrival.time).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(offer.return_flight.arrival.time).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          })}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          Duration
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {offer.return_flight.duration || 'N/A'}
                        </Typography>
                        {offer.return_flight.airline && (
                          <Typography variant="caption" color="text.secondary">
                            {offer.return_flight.airline}
                          </Typography>
                        )}
                        {offer.return_flight.flight_number && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Flight {offer.return_flight.flight_number}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" color="text.secondary">
                          Stops
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {offer.return_flight.stops !== undefined ? `${offer.return_flight.stops} ${offer.return_flight.stops === 1 ? 'stop' : 'stops'}` : 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                        </Box>
                      )}
              </Stack>
            </Paper>}

            {/* Fare rules, baggage, and taxes */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Stack spacing={2}>
                  {/* Fare Rules */}
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>Fare rules</Typography>
                      <Typography variant="body2" color="text.secondary">
                      {offer.conditions ? (
                        <>
                          {offer.conditions.refund_before_departure ? 'Refundable before departure' : 'Non‑refundable before departure'}
                          {offer.conditions.change_before_departure !== undefined && ` • ${offer.conditions.change_before_departure ? 'Changes allowed' : 'Changes not allowed'}`}
                        </>
                      ) : (
                        'Fare rules will be displayed at checkout'
                      )}
                      </Typography>
                    </Box>

                  {/* Baggage Allowance - Always show */}
                    <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Baggage Allowance
                    </Typography>
                    {offer.baggage_allowances && Array.isArray(offer.baggage_allowances) && offer.baggage_allowances.length > 0 ? (
                        <Stack spacing={1}>
                          {offer.baggage_allowances.map((baggage, idx) => (
                            <Box key={idx} sx={{ pl: 2, borderLeft: '3px solid #6366f1' }}>
                              {baggage.passenger_id && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  Passenger {baggage.passenger_id}
                                </Typography>
                              )}
                              <Grid container spacing={2}>
                                {/* Carry-on */}
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                                    ✓ Carry-on Bag
                                  </Typography>
                      <Typography variant="body2" color="text.secondary">
                                    {baggage.carry_on_quantity || 1} bag{(baggage.carry_on_quantity || 1) > 1 ? 's' : ''}
                                    {baggage.carry_on_weight && ` (${baggage.carry_on_weight} kg)`}
                      </Typography>
                                </Grid>
                                {/* Checked bag */}
                                <Grid item xs={12} sm={6}>
                                  {baggage.checked_bag_quantity > 0 ? (
                                    <>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                                        ✓ Checked Bag
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {baggage.checked_bag_quantity} bag{baggage.checked_bag_quantity > 1 ? 's' : ''}
                                        {baggage.checked_bag_weight && ` (${baggage.checked_bag_weight} kg each)`}
                                      </Typography>
                                    </>
                                  ) : (
                                    <>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#dc2626' }}>
                                        ✗ No Checked Bag
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        Purchase separately
                                      </Typography>
                                    </>
                                  )}
                                </Grid>
                              </Grid>
                    </Box>
                          ))}
                        </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Baggage information will be confirmed after booking. Standard airline baggage rules apply.
                      </Typography>
                    )}
                    </Box>
                  {/* Taxes & Fees */}
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>Taxes & fees</Typography>
                    {offer.price_breakdown ? (
                      <>
                      <Typography variant="body2" color="text.secondary">
                        Total: {offer.price_breakdown.total_currency} {offer.price_breakdown.total_amount}
                        {offer.price_breakdown.base_amount && ` • Base: ${offer.price_breakdown.total_currency} ${offer.price_breakdown.base_amount}`}
                      </Typography>
                      {Array.isArray(offer.price_breakdown.tax_amounts) && offer.price_breakdown.tax_amounts.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          {offer.price_breakdown.tax_amounts.map((t, i) => (
                            <Typography key={i} variant="body2" color="text.secondary">
                              {t.type || 'Tax'}: {t.currency || offer.price_breakdown.total_currency} {t.amount || t.total_amount}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Tax breakdown will be displayed at checkout
                      </Typography>
                      )}
                    </Box>
                </Stack>
              </Paper>

              {/* Fare class already selected on previous page - no need for duplicate selector */}

              {/* Extra Baggage Section */}
              <Paper sx={{ p: 3, mt: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Luggage />
                    <Typography variant="h6" fontWeight={600}>
                      Extra Baggage
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Luggage />}
                    onClick={() => setBaggageSelectorOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Add Baggage
                  </Button>
                </Box>

                {selectedBaggage.length > 0 ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Selected extra baggage:
                    </Typography>
                    {selectedBaggage.map((bag, idx) => (
                      <Chip
                        key={idx}
                        label={`${bag.quantity}x ${bag.type === 'baggage' ? 'Checked bag' : bag.type} (+${new Intl.NumberFormat('en-US', { style: 'currency', currency: bag.total_currency }).format(parseFloat(bag.total_amount) * bag.quantity)})`}
                        onDelete={() => {
                          setSelectedBaggage(selectedBaggage.filter((_, i) => i !== idx));
                        }}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No extra baggage selected. Your ticket includes standard baggage allowance.
                  </Typography>
                )}
              </Paper>

                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Passenger{passengers.length > 1 ? 's' : ''} ({passengers.length})</Typography>
                  {passengers.map((p, idx) => (
                    <Paper key={`p-${idx}`} variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Passenger {idx + 1}
                      </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth>
                          <InputLabel id={`title-label-${idx}`}>Title</InputLabel>
                          <Select labelId={`title-label-${idx}`} label="Title" name="title" value={p.title} onChange={(e) => handleChange(idx, e)}>
                            <MenuItem value="Mr">Mr</MenuItem>
                            <MenuItem value="Ms">Ms</MenuItem>
                            <MenuItem value="Mrs">Mrs</MenuItem>
                            <MenuItem value="Mx">Mx</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth>
                          <InputLabel id={`gender-label-${idx}`}>Gender</InputLabel>
                          <Select labelId={`gender-label-${idx}`} label="Gender" name="gender" value={p.gender} onChange={(e) => handleChange(idx, e)}>
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="unspecified">Unspecified</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            required
                            label="First Name" 
                            name="firstName" 
                            value={p.firstName} 
                            onChange={(e) => handleChange(idx, e)}
                            onBlur={(e) => handleBlur(idx, e)}
                            error={!!validationErrors[idx]?.firstName}
                            helperText={validationErrors[idx]?.firstName || 'Enter passenger\'s first name'}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            required
                            label="Last Name" 
                            name="lastName" 
                            value={p.lastName} 
                            onChange={(e) => handleChange(idx, e)}
                            onBlur={(e) => handleBlur(idx, e)}
                            error={!!validationErrors[idx]?.lastName}
                            helperText={validationErrors[idx]?.lastName || 'Enter passenger\'s last name'}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            required
                            label="Email" 
                            name="email" 
                            type="email"
                            value={p.email} 
                            onChange={(e) => handleChange(idx, e)}
                            onBlur={(e) => handleBlur(idx, e)}
                            error={!!validationErrors[idx]?.email}
                            helperText={validationErrors[idx]?.email || 'Valid email address'}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            required
                            label="Phone" 
                            name="phone" 
                            value={p.phone} 
                            onChange={(e) => handleChange(idx, e)}
                            onBlur={(e) => handleBlur(idx, e)}
                            error={!!validationErrors[idx]?.phone}
                            placeholder="+15551234567"
                            helperText={validationErrors[idx]?.phone || 'Include country code (e.g., +1 for US)'}
                          />
                        </Grid>
                      <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            required
                            label="Date of Birth" 
                            name="dateOfBirth" 
                            type="date"
                            value={p.dateOfBirth} 
                            onChange={(e) => handleChange(idx, e)}
                            onBlur={(e) => handleBlur(idx, e)}
                            error={!!validationErrors[idx]?.dateOfBirth}
                            helperText={validationErrors[idx]?.dateOfBirth || 'Must be 18+ years old (YYYY-MM-DD)'}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}

                {/* ========== PRICING BREAKDOWN ========== */}
                {customerPrice && (
                  <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f0f9ff', border: '2px solid #6366f1' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Total Cost
                  </Typography>
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1" color="text.secondary">
                          Flight Cost
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {currency} {unitAmount.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1" color="text.secondary">
                          Service Fee ({MARKUP * 100}%)
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {currency} {(unitAmount * MARKUP).toFixed(2)}
                  </Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Total
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#6366f1' }}>
                          {currency} {customerPrice}
                        </Typography>
                      </Box>
                      {passengers.length > 1 && (
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                          {currency} {(parseFloat(customerPrice) / passengers.length).toFixed(2)} per passenger × {passengers.length} passengers
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                )}

                {/* Important Alerts */}
                {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>🧪 Local Dev Mode:</strong> Stripe will show for UI testing. Use test card <code>4242 4242 4242 4242</code>. Webhooks won't work locally - booking may not complete automatically.
                      </Alert>
                )}
                
                <Alert severity="warning" sx={{ mb: 2, borderLeft: '4px solid #ff9800' }}>
                  <strong>⏰ Time Sensitive:</strong> Flight offers expire in 2-5 minutes (test mode) or 15-20 minutes (production). Complete your booking promptly to lock in this price. If expired, simply search again.
                </Alert>

            <Box sx={{ mt: 3 }}>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={handleBook} 
                      disabled={loading}
                      fullWidth
                      size="large"
                      sx={{ 
                        py: 2, 
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                        }
                      }}
                    >
                      {loading ? t('flightBooking.processing') : t('flightBooking.bookNow', { currency, amount: customerPrice || totalAmount.toFixed(2) })}
                    </Button>
            </Box>
            
            {/* ========== STRIPE CHECKOUT MODAL ========== */}
            {stripeCheckoutOpen && clientSecret && (
              <StripeCheckout
                open={stripeCheckoutOpen}
                clientSecret={clientSecret}
                onSuccess={handlePaymentSuccess}
                onClose={() => setStripeCheckoutOpen(false)}
                amount={customerPrice}
                currency={currency}
                upgradeType="Flight Booking"
              />
            )}
            
            {/* ========== BAGGAGE SELECTOR MODAL ========== */}
            <BaggageSelector
              open={baggageSelectorOpen}
              onClose={() => setBaggageSelectorOpen(false)}
              flight={{
                destination: offer?.destination || '',
                departure_date: offer?.departure_time || '',
                included_baggage: offer?.baggage_allowances?.length > 0 
                  ? `${offer.baggage_allowances.length} checked bag(s) included with ticket`
                  : '1 cabin bag and 1 checked bag included with ticket',
                currency: offer?.total_currency || 'USD'
              }}
              passengers={passengers}
              availableServices={availableServices}
              onConfirm={(selectedServices) => {
                setSelectedBaggage(selectedServices);
                console.log('✅ Selected baggage:', selectedServices);
              }}
            />
            
            {/* ========== AUTH MODAL ========== */}
            <AuthModal
              open={authModalOpen}
              onClose={() => setAuthModalOpen(false)}
              onSuccess={() => {
                // After successful login/signup, automatically retry booking
                setAuthModalOpen(false);
                handleBook();
              }}
              message="Sign in to complete your booking"
            />
              </>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
}

export default function DuffelOffer() { return <DuffelOfferInner />; }


